import { useState } from "react";
import { cx } from "../cx";
import { Icon } from "../Icon";
import { Popover, useDisclosure } from "../Popover";
import styles from "./DatePicker.module.css";
import {
  addMonths,
  DATE_PRESETS,
  type DateRange,
  formatDateDefault,
  matchPreset,
  presetRange,
  shiftRange,
  startOfMonth,
  type WeekStart,
} from "./dates";
import { MonthCalendar } from "./MonthCalendar";

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  weekStart?: WeekStart;
  format?: (d: Date) => string;
  /** The ‹ › buttons beside the trigger that step the range by its own length. */
  showArrows?: boolean;
  /** Injected for tests; defaults to now. */
  today?: Date;
  className?: string;
}

export function DateRangePicker({
  value,
  onChange,
  weekStart = "MONDAY",
  format = formatDateDefault,
  showArrows = true,
  today = new Date(),
  className,
}: DateRangePickerProps) {
  const { open, toggle, close, anchorRef } = useDisclosure();
  const [month, setMonth] = useState(() => startOfMonth(value.start));
  const [anchorDay, setAnchorDay] = useState<Date | null>(null);
  const [hoverDay, setHoverDay] = useState<Date | null>(null);

  const preset = matchPreset(value, today, weekStart);
  const label = preset
    ? (DATE_PRESETS.find((p) => p.key === preset)?.label ?? "")
    : `${format(value.start)} - ${format(value.end)}`;

  const commit = (r: DateRange) => {
    onChange(r);
    setAnchorDay(null);
    close();
  };

  // First click fixes one end, second click the other; picking backwards just swaps them.
  const onPick = (d: Date) => {
    if (!anchorDay) {
      setAnchorDay(d);
      return;
    }
    commit(d < anchorDay ? { start: d, end: anchorDay } : { start: anchorDay, end: d });
  };

  const preview =
    anchorDay && hoverDay
      ? hoverDay < anchorDay
        ? { start: hoverDay, end: anchorDay }
        : { start: anchorDay, end: hoverDay }
      : anchorDay
        ? { start: anchorDay, end: anchorDay }
        : null;

  return (
    <div className={cx(styles.rangeRoot, className)}>
      <button
        ref={anchorRef}
        type="button"
        className={cx(styles.trigger, styles.rangeTrigger)}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open) {
            setMonth(startOfMonth(addMonths(value.end, -1)));
            setAnchorDay(null);
          }
          toggle();
        }}
      >
        <Icon name="calendar" size={16} className={styles.triggerIcon} />
        <span>{label}</span>
      </button>
      {showArrows && (
        <>
          <button
            type="button"
            className={styles.shift}
            aria-label="Previous period"
            onClick={() => onChange(shiftRange(value, -1))}
          >
            <Icon name="chevron-left" size={16} />
          </button>
          <button
            type="button"
            className={styles.shift}
            aria-label="Next period"
            onClick={() => onChange(shiftRange(value, 1))}
          >
            <Icon name="chevron-right" size={16} />
          </button>
        </>
      )}
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        surface="card"
        className={cx(styles.panel, styles.rangePanel)}
        role="dialog"
        aria-label="Select date range"
      >
        <ul className={styles.presets}>
          {DATE_PRESETS.map((p) => (
            <li key={p.key}>
              <button
                type="button"
                className={cx(styles.preset, preset === p.key && styles.presetActive)}
                onClick={() => commit(presetRange(p.key, today, weekStart))}
              >
                {p.label}
              </button>
            </li>
          ))}
        </ul>
        <div className={styles.months} onPointerLeave={() => setHoverDay(null)}>
          <MonthCalendar
            month={month}
            weekStart={weekStart}
            today={today}
            selected={value}
            preview={preview}
            onPick={onPick}
            onHover={anchorDay ? setHoverDay : undefined}
            onPrev={() => setMonth((m) => addMonths(m, -1))}
          />
          <MonthCalendar
            month={addMonths(month, 1)}
            weekStart={weekStart}
            today={today}
            selected={value}
            preview={preview}
            onPick={onPick}
            onHover={anchorDay ? setHoverDay : undefined}
            onNext={() => setMonth((m) => addMonths(m, 1))}
          />
        </div>
      </Popover>
    </div>
  );
}
